<template>
  <b-card class="rounded-lg">
    <div class="fp-print-only">
      <h2 class="mb-25">
        {{ companyName }}
      </h2>
      <h3 class="mb-25">
        {{ $t('ledger_fp_statement') }}
      </h3>
      <p class="mb-25">
        {{ displayDate(report && report.as_of) }}
      </p>
      <p class="mb-25">
        {{ printScope }}
      </p>
      <p class="mb-25">
        {{ $t('ledger_fp_printed_by') }}: {{ printedBy }}
      </p>
      <p class="mb-1">
        {{ $t('ledger_fp_printed_at') }}: {{ printedAt }}
      </p>
    </div>

    <div class="fp-no-print">
      <report-header
        :title="$t('ledger_fp_title')"
        :subtitle="subtitle"
        :scope-label="scopeLabel"
        :can-company="canCompany"
        :company="company"
        @company="company = $event; load()"
        @print="printSheet"
      >
        <template #filters>
          <div class="d-flex flex-wrap align-items-end mt-50">
            <b-form-group
              :label="$t('ledger_fp_date')"
              class="mr-50 mb-50"
            >
              <b-form-input
                v-model="asOf"
                type="date"
                class="fp-date"
                @change="load"
              />
            </b-form-group>
            <b-form-group
              :label="$t('ledger_fp_compare')"
              class="mr-50 mb-50"
            >
              <b-form-input
                v-model="compareTo"
                type="date"
                class="fp-date"
                @change="load"
              />
            </b-form-group>
            <b-button
              size="sm"
              variant="outline-primary"
              class="mb-50 mr-50"
              @click="usePreviousYearEnd"
            >
              {{ $t('ledger_fp_prev_year') }}
            </b-button>
            <b-button
              size="sm"
              variant="outline-primary"
              class="mb-50 mr-50"
              @click="usePreviousMonthEnd"
            >
              {{ $t('ledger_fp_prev_month') }}
            </b-button>
            <b-button
              v-if="compareTo"
              size="sm"
              variant="outline-secondary"
              class="mb-50 mr-50"
              @click="clearCompare"
            >
              {{ $t('ledger_fp_clear_compare') }}
            </b-button>
            <b-button
              size="sm"
              variant="primary"
              class="mb-50"
              @click="load"
            >
              {{ $t('ledger_assets_load') }}
            </b-button>
          </div>
        </template>
      </report-header>

      <div
        v-if="loading"
        class="fp-sides"
      >
        <div
          v-for="column in 2"
          :key="column"
        >
          <b-skeleton
            width="40%"
            class="mb-1"
          />
          <b-skeleton
            v-for="row in 8"
            :key="row"
            class="mb-50"
          />
        </div>
      </div>

      <div
        v-else-if="report && report.totals"
        class="fp-cards"
      >
        <div class="fp-card">
          <span>{{ $t('ledger_fp_card_assets') }}</span>
          <strong class="fp-num">{{ formatAmount(report.totals.total_assets) }}</strong>
        </div>
        <div class="fp-card">
          <span>{{ $t('ledger_fp_card_liabilities') }}</span>
          <strong class="fp-num">{{ formatAmount(report.totals.total_liabilities) }}</strong>
        </div>
        <div class="fp-card">
          <span>{{ $t('ledger_fp_card_equity') }}</span>
          <strong class="fp-num">{{ formatAmount(report.totals.equity) }}</strong>
        </div>
        <div
          v-if="report.profit_for_year"
          class="fp-card"
        >
          <span>{{ $t('ledger_fp_card_profit') }}</span>
          <strong class="fp-num">{{ formatAmount(report.profit_for_year.net_profit) }}</strong>
        </div>
      </div>
    </div>

    <financial-position-sheet
      v-if="!loading && report"
      :report="report"
    />
  </b-card>
</template>

<script>
import {
  BCard, BFormGroup, BFormInput, BButton, BSkeleton,
} from 'bootstrap-vue'
import { getFinancialPosition } from '@/api/accounting/financialReports'
import { oneFrom } from '@/utils/ledger'
import { getUserData } from '@/auth/utils'
import ReportHeader from './ReportHeader.vue'
import FinancialPositionSheet from './FinancialPositionSheet.vue'
import reportMixin from './reportMixin'

export default {
  name: 'FinancialPosition',
  components: {
    BCard, BFormGroup, BFormInput, BButton, BSkeleton, ReportHeader, FinancialPositionSheet,
  },
  mixins: [reportMixin],
  data() {
    return {
      loading: true,
      compareTo: '',
      printedAt: '',
    }
  },
  computed: {
    subtitle() {
      const date = this.displayDate((this.report && this.report.as_of) || this.asOf)
      return this.$t('ledger_fp_as_of', { date })
    },
    companyName() {
      const user = getUserData() || {}
      return user.company_name || (user.company && user.company.name) || this.$t('ledger_fp_company')
    },
    printedBy() {
      const user = getUserData() || {}
      return user.name || ''
    },
    printScope() {
      const scope = this.report && this.report.scope
      if (scope && scope.scope === 'company') return this.$t('ledger_rep_company_level')
      const user = getUserData() || {}
      return user.branch_name || (user.branch && user.branch.name) || this.$t('ledger_rep_branch')
    },
  },
  created() {
    const query = this.$route.query || {}
    if (typeof query.as_of === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(query.as_of)) this.asOf = query.as_of
    if (typeof query.compare_to === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(query.compare_to)) {
      this.compareTo = query.compare_to
    }
    if (query.scope === 'company' && this.canCompany) this.company = true
  },
  mounted() {
    this.load()
  },
  methods: {
    displayDate(iso) {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''))
      if (!match) return ''
      return `${match[3]}/${match[2]}/${match[1]}`
    },
    formatAmount(value) {
      const n = Number(value)
      const rounded = Number.isFinite(n) ? Math.round(n * 100) / 100 : 0
      const loc = this.$i18n.locale === 'en' ? 'en-EG' : 'ar-EG'
      const text = new Intl.NumberFormat(loc, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Math.abs(rounded))
      return rounded < 0 ? `(${text})` : text
    },
    previousYearEnd(iso) {
      const year = Number(String(iso || '').slice(0, 4))
      const y = Number.isFinite(year) && year > 0 ? year : new Date().getFullYear()
      return `${y - 1}-12-31`
    },
    previousMonthEnd(iso) {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '')
      const now = new Date()
      const year = match ? Number(match[1]) : now.getFullYear()
      const month = match ? Number(match[2]) : now.getMonth() + 1
      const date = new Date(year, month - 1, 0)
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${date.getFullYear()}-${m}-${d}`
    },
    usePreviousYearEnd() {
      this.compareTo = this.previousYearEnd(this.asOf)
      this.load()
    },
    usePreviousMonthEnd() {
      this.compareTo = this.previousMonthEnd(this.asOf)
      this.load()
    },
    clearCompare() {
      this.compareTo = ''
      this.load()
    },
    printSheet() {
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      this.printedAt = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`
      this.$nextTick(() => {
        window.print()
      })
    },
    async load() {
      if (!this.asOf) return
      this.loading = true
      try {
        const params = { as_of: this.asOf, ...this.scopeParams }
        if (this.compareTo) params.compare_to = this.compareTo
        const res = await getFinancialPosition(params)
        this.report = oneFrom(res)
      } catch (error) {
        this.toastError(error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.fp-print-only { display: none; }
.fp-date { max-width: 11rem; }
.fp-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.fp-card {
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.fp-num {
  font-variant-numeric: tabular-nums;
  text-align: end;
}
.fp-sides { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
@media (max-width: 991.98px) {
  .fp-cards,
  .fp-sides { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 575.98px) {
  .fp-cards { grid-template-columns: 1fr; }
}
@media print {
  .fp-no-print { display: none !important; }
  .fp-print-only { display: block !important; }
}
</style>

<style>
@media print {
  @page { size: A4 portrait; margin: 12mm; }
}
</style>
